import { isBoolean, isNull, isNumber, isString } from 'lodash';

import {
  DEFAULT_QUERY_OPERATOR_LABELS,
  QUERY_BUILDER_ARRAY_OPERATORS,
  QUERY_BUILDER_ARRAY_OR_STRING_OPERATORS,
  QueryBuilderOperator,
} from './query-builder-operators';
import {
  BetweenRule,
  BetweenValue,
  JsonLogic,
  JsonLogicOperator,
  NotBetweenRule,
  QueryBuilderValue,
  StandardRule,
  VarRule,
} from './type';

export type ValidationError = {
  path: string;
  reasons: string[];
};

type ExtractedRule = {
  var: string;
  value: QueryBuilderValue;
  operator: JsonLogicOperator;
};

const validateJsonLogicRule = (
  rule: ExtractedRule,
  validFields?: string[],
): string[] | null => {
  const fieldName = rule.var;
  const queryBuilderOperatorLike = rule.operator === '==' ? '=' : rule.operator;
  const operatorLabel =
    DEFAULT_QUERY_OPERATOR_LABELS[
      queryBuilderOperatorLike as QueryBuilderOperator
    ] || rule.operator;

  // Validate field name if validFields is provided
  if (validFields && !validFields.includes(fieldName)) {
    return [`Field "${fieldName}" is not a valid field`];
  }

  if (
    QUERY_BUILDER_ARRAY_OPERATORS.includes(
      rule.operator as QueryBuilderOperator,
    )
  ) {
    if (!Array.isArray(rule.value) || !rule.value.length) {
      return [
        `Field "${fieldName}" with operator "${operatorLabel}" requires at least one value`,
      ];
    }

    return null;
  }

  if (
    QUERY_BUILDER_ARRAY_OR_STRING_OPERATORS.includes(
      rule.operator as QueryBuilderOperator,
    )
  ) {
    if (Array.isArray(rule.value) && !rule.value.length) {
      return [
        `Field "${fieldName}" with operator "${operatorLabel}" requires at least one value`,
      ];
    }

    if (isString(rule.value) && !rule.value.length) {
      return [
        `Field "${fieldName}" with operator "${operatorLabel}" requires a value`,
      ];
    }

    if (!Array.isArray(rule.value) && !isString(rule.value)) {
      return [
        `Operator "${operatorLabel}" requires a string or an array value`,
      ];
    }

    return null;
  }

  // @NOTE this is a special case for null/notNull operators
  if (
    (rule.operator === '==' || rule.operator === '!=') &&
    isNull(rule.value)
  ) {
    return null;
  }

  if (!rule.value && !isNumber(rule.value) && !isBoolean(rule.value)) {
    return [
      `Field "${fieldName}" with operator "${operatorLabel}" requires a value`,
    ];
  }

  return null;
};

const isVarRule = (value: unknown): value is VarRule => {
  return (
    typeof value === 'object' &&
    value !== null &&
    'var' in value &&
    typeof (value as { var: unknown }).var === 'string'
  );
};

const isBetweenRule = (
  jsonLogic: JsonLogic,
): jsonLogic is BetweenRule | NotBetweenRule => {
  if (!jsonLogic || typeof jsonLogic !== 'object') return false;

  // Check for between pattern: <= with 3 values
  if ('<=' in jsonLogic) {
    const values = jsonLogic['<='];
    return Array.isArray(values) && values.length === 3;
  }

  // Check for notBetween pattern: ! with <= and 3 values
  if ('!' in jsonLogic) {
    const notValue = jsonLogic['!'];
    return (
      typeof notValue === 'object' &&
      notValue !== null &&
      '<=' in notValue &&
      Array.isArray(notValue['<=']) &&
      notValue['<='].length === 3
    );
  }

  return false;
};

const validateBetweenRule = (jsonLogic: JsonLogic): string[] | null => {
  if (!isBetweenRule(jsonLogic)) return ['Invalid between operation format'];

  const values =
    '<=' in jsonLogic
      ? jsonLogic['<=']
      : (jsonLogic['!'] as { '<=': unknown[] })['<='];

  if (!Array.isArray(values) || values.length !== 3) {
    return ['Invalid between operation format'];
  }

  const [min, varOrValue, max] = values;
  if (!isVarRule(varOrValue)) {
    return [
      'Invalid between operation format: middle value must be a field reference',
    ];
  }

  // Add validation for null min/max values
  if (min === null || max === null) {
    return ['Between operation does not accept null values for min/max bounds'];
  }

  // Ensure min/max are string or number
  if (
    (typeof min !== 'string' && typeof min !== 'number') ||
    (typeof max !== 'string' && typeof max !== 'number')
  ) {
    return ['Between operation bounds must be string or number values'];
  }

  return null;
};

const extractRuleFromJsonLogic = (
  jsonLogic: JsonLogic,
): ExtractedRule | null => {
  if (!jsonLogic || typeof jsonLogic !== 'object') return null;

  // Handle between rules
  if (isBetweenRule(jsonLogic)) {
    if ('!' in jsonLogic) {
      const [min, varOrValue, max] = jsonLogic['!']['<='];
      if (!isVarRule(varOrValue)) return null;
      return {
        operator: '!',
        value: [min, max] as [BetweenValue, BetweenValue],
        var: varOrValue.var,
      };
    } else {
      const [min, varOrValue, max] = jsonLogic['<='];
      if (!isVarRule(varOrValue)) return null;
      return {
        operator: '<=',
        value: [min, max] as [BetweenValue, BetweenValue],
        var: varOrValue.var,
      };
    }
  }

  const rule = jsonLogic as StandardRule;
  const operator = Object.keys(rule)[0] as JsonLogicOperator;
  const operatorValue = rule[operator];

  if (!Array.isArray(operatorValue) || operatorValue.length !== 2) return null;

  const varObjIndex = operatorValue.findIndex(isVarRule);
  if (varObjIndex === -1) return null;

  const valueIndex = varObjIndex === 0 ? 1 : 0;
  const varObj = operatorValue[varObjIndex] as VarRule;
  const value = operatorValue[valueIndex] as QueryBuilderValue;

  return {
    operator,
    value,
    var: varObj.var,
  };
};

const validateJsonLogic = (
  jsonLogic: JsonLogic,
  validFields?: string[],
): ValidationError[] => {
  const errors: ValidationError[] = [];

  if (!jsonLogic || typeof jsonLogic !== 'object') {
    return errors;
  }

  // Handle root level combinators (and/or)
  const combinator = Object.keys(jsonLogic)[0];
  if (combinator === 'and' || combinator === 'or') {
    const rules = jsonLogic[combinator as keyof JsonLogic] as JsonLogic[];
    if (!Array.isArray(rules) || !rules.length) {
      errors.push({
        path: 'root',
        reasons: ['Query group cannot be empty. Add at least one condition.'],
      });
      return errors;
    }

    // Validate each rule in the group
    rules.forEach((rule, index) => {
      // Handle nested combinators
      if (rule && typeof rule === 'object') {
        const nestedCombinator = Object.keys(rule)[0];
        if (nestedCombinator === 'and' || nestedCombinator === 'or') {
          const nestedErrors = validateJsonLogic(rule, validFields);
          if (nestedErrors.length > 0) {
            errors.push(
              ...nestedErrors.map((error) => ({
                path: `group_${index}.${error.path}`,
                reasons: error.reasons,
              })),
            );
          }
        } else {
          // Check for between rule first
          if (isBetweenRule(rule)) {
            const betweenErrors = validateBetweenRule(rule);
            if (betweenErrors) {
              errors.push({
                path: `rule_${index}`,
                reasons: betweenErrors,
              });
              return;
            }
          }

          // Handle individual rule
          const extractedRule = extractRuleFromJsonLogic(rule);
          if (extractedRule) {
            const ruleErrors = validateJsonLogicRule(
              extractedRule,
              validFields,
            );
            if (ruleErrors) {
              errors.push({
                path: `rule_${index}`,
                reasons: ruleErrors,
              });
            }
          }
        }
      }
    });
  } else {
    // Check for between rule first
    if (isBetweenRule(jsonLogic)) {
      const betweenErrors = validateBetweenRule(jsonLogic);
      if (betweenErrors) {
        errors.push({
          path: 'rule_0',
          reasons: betweenErrors,
        });
        return errors;
      }
    }

    // Handle single rule
    const extractedRule = extractRuleFromJsonLogic(jsonLogic);
    if (extractedRule) {
      const ruleErrors = validateJsonLogicRule(extractedRule, validFields);
      if (ruleErrors) {
        errors.push({
          path: 'rule_0',
          reasons: ruleErrors,
        });
      }
    }
  }

  return errors;
};

// @NOTE This is a validator specific to the query builder component
// We have a similar validator for the query builder rule format: @dotfile/frontend/shared/common/src/lib/query-builder/rule-group-validator.ts
// Any changes here should be reflected in the rule-group-validator.ts file if necessary
export const jsonLogicValidator = (
  jsonLogic: JsonLogic,
  validFields?: string[],
): ValidationError[] => {
  return validateJsonLogic(jsonLogic, validFields);
};
